import {
    defaultLogoutProps,
    ILogoutProps
} from '~/controllers/base-session.controller';
import { IUserResponse } from '~/types/server.types';
import { IWeakLoginProps } from '../stores/cx-flow-session.store';
import type { User } from '~/models/user.model';
import { ForceStrongAuthReason } from '~/components/ClientExperience/OneFlow/stores/flow-view.ui.store';
import { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';

export class FlowSessionCXCtrl {
    rootStore: CXRootStore;

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;
    }

    async CXAuthenticate({
        userIdFromUrl,
        onForceAuth
    }: { userIdFromUrl?: string; onForceAuth?: () => void } = {}) {
        const { sessionCtrl, flowCtrl, flowViewUiStore } = this.rootStore;
        const {
            loggedUser,
            loggedUserFromJStorage,
            loggedUserFromLocalStorage
        } = this.getUserFromCrossLocalStorage();

        if (userIdFromUrl) {
            await this.loginUserByUserIdFromUrl({
                userIdFromUrl,
                loggedUser,
                loggedUserFromJStorage,
                onForceAuth
            });

            return;
        }

        if (!loggedUserFromLocalStorage?._id) {
            this.weakLoginFromLocalStorage();

            await flowCtrl.userValidateWeakAuth({
                flowId: flowViewUiStore.flowId,
                reason: ForceStrongAuthReason.clientExperienceMemberAccessOnLand,
                onForceAuth
            });

            return;
        }

        sessionCtrl.login(loggedUserFromLocalStorage);
    }

    private async loginUserByUserIdFromUrl({
        userIdFromUrl,
        loggedUser,
        loggedUserFromJStorage,
        onForceAuth
    }: {
        userIdFromUrl: string;
        loggedUser: IUserResponse;
        loggedUserFromJStorage: IUserResponse;
        onForceAuth?: () => void;
    }) {
        const { flowCtrl, flowViewUiStore, sessionCtrl } = this.rootStore;

        if (loggedUser && loggedUser._id === userIdFromUrl) {
            sessionCtrl.login(loggedUser);

            return;
        }

        if (
            loggedUserFromJStorage &&
            loggedUserFromJStorage._id === userIdFromUrl
        ) {
            sessionCtrl.login(loggedUserFromJStorage);

            return;
        }

        this.weakLogin({ userId: userIdFromUrl });

        await flowCtrl.userValidateWeakAuth({
            flowId: flowViewUiStore.flowId,
            reason: ForceStrongAuthReason.clientExperienceMemberAccessOnLand,
            onForceAuth
        });
    }

    weakLoginFromLocalStorage() {
        const { sessionStore, flowSessionStore, storageService } =
            this.rootStore;

        const weakAuthUser = JSON.parse(
            storageService.getItem(sessionStore.limitedAuthStorageKey)
        );

        if (flowSessionStore.isAuthorized || !weakAuthUser) {
            return;
        }

        this.weakLogin({
            userEmail: weakAuthUser.email,
            userId: weakAuthUser._id,
            isRealChargeableUser: weakAuthUser.is_real_chargeable_user
        });
    }

    async logout(localLogoutProps: ILogoutProps = defaultLogoutProps) {
        const { sessionStore, sessionCtrl } = this.rootStore;
        const { authenticated } = sessionStore;

        this.weakLogout();

        if (authenticated) {
            sessionCtrl.logout(localLogoutProps);

            return;
        }

        await this.forceLogout();
    }

    async forceLogout() {
        const { sessionService, apiService } = this.rootStore;

        const { loggedUserFromLocalStorage } =
            this.getUserFromCrossLocalStorage();

        if (!loggedUserFromLocalStorage) {
            return;
        }

        await sessionService.logout({
            email: loggedUserFromLocalStorage.email,
            headers: apiService.getStrongUserHeaderCredentials(
                loggedUserFromLocalStorage as Partial<User>
            )
        });
        this.localLogout({ forceCrossLogout: true });
    }

    localLogout({
        forceCrossLogout = defaultLogoutProps.forceCrossLogout
    }: ILogoutProps = defaultLogoutProps) {
        this.rootStore.sessionCtrl.localLogout({
            forceCrossLogout
        });

        this.weakLogoutFromStorage();
    }

    weakLogin({ userEmail, userId, isRealChargeableUser }: IWeakLoginProps) {
        const { flowSessionStore } = this.rootStore;

        if (userEmail) {
            flowSessionStore.setUserEmail(userEmail);
        }

        if (userId) {
            flowSessionStore.setUserId(userId);
        }

        flowSessionStore.setRealChargeableUser(isRealChargeableUser);

        if (!flowSessionStore.userWeakAuthData) {
            return;
        }

        this.setWeakTokenInStorage(flowSessionStore.userWeakAuthData);
    }

    setWeakTokenInStorage(user: Partial<User>) {
        const { _id, email, is_real_chargeable_user } = user;

        const authData = {
            _id,
            email,
            is_real_chargeable_user
        };

        const { sessionStore, storageService } = this.rootStore;

        const { hash } =
            JSON.parse(
                storageService.getItem(sessionStore.limitedAuthStorageKey)
            ) || {};

        storageService.setItem(
            sessionStore.limitedAuthStorageKey,
            JSON.stringify({
                hash,
                ...authData
            })
        );
    }

    weakLogout() {
        const { flowSessionStore } = this.rootStore;

        flowSessionStore.setUserEmail(null);
        flowSessionStore.setUserId(null);
        flowSessionStore.setRealChargeableUser(false);

        if (!flowSessionStore.limitedAuthStorageKey) {
            return;
        }

        this.weakLogoutFromStorage();
    }

    weakLogoutFromStorage() {
        this.setWeakTokenInStorage({});
    }

    getUserFromCrossLocalStorage() {
        const { jStorageService, sessionCtrl } = this.rootStore;

        const loggedUser = sessionCtrl.getUserFromLocalStorage();
        const loggedUserFromJStorage = jStorageService.get(
            'HB_CURR_USER'
        ) as IUserResponse;

        return {
            loggedUser,
            loggedUserFromJStorage,
            loggedUserFromLocalStorage: loggedUser || loggedUserFromJStorage
        };
    }
}
